import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useSupply } from "../context/SupplyContext";

const statusStyles = {
    "Automatisch geprüft": "bg-green-100 text-green-800 border-green-200",
    "Offen": "bg-amber-100 text-amber-800 border-amber-200",
    "In Klärung": "bg-orange-100 text-orange-800 border-orange-200",
    "Abgeschlossen": "bg-blue-100 text-blue-800 border-blue-200",
};

const importSourceLabels = {
    TecCom: {
        label: "TecCom",
        classes: "bg-blue-50 text-blue-700 border-blue-100",
    },
    Manuell: {
        label: "Manuell",
        classes: "bg-slate-50 text-slate-700 border-slate-200",
    },
};

const dateFormatter = new Intl.DateTimeFormat("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
});

const currencyFormatter = new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
});

function InvoiceReviewListPage() {
    const { invoices } = useSupply();

    const sortedInvoices = useMemo(
        () =>
            [...invoices].sort(
                (a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime(),
            ),
        [invoices],
    );

    const summary = useMemo(() => {
        const total = invoices.length;
        const autoMatched = invoices.filter((invoice) => invoice.status === "Automatisch geprüft").length;
        const open = invoices.filter((invoice) => invoice.status === "Offen").length;
        const clarification = invoices.filter((invoice) => invoice.status === "In Klärung").length;
        const finished = invoices.filter((invoice) => invoice.status === "Abgeschlossen").length;

        return { total, autoMatched, open, clarification, finished };
    }, [invoices]);

    return (
        <div className="mx-auto max-w-6xl px-4 py-8">
            <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-3xl font-semibold text-slate-900">Rechnungsprüfung</h1>
                    <p className="mt-2 text-sm text-slate-600">
                        Übersicht neuer Lieferantenrechnungen aus TecCom und manueller Erfassung.
                    </p>
                </div>
                <div className="flex gap-2">
                    <span className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600">
                        {summary.total} offene Eingänge
                    </span>
                    <span className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600">
                        {summary.autoMatched} automatisch geprüft
                    </span>
                </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <SummaryCard label="Gesamt" value={summary.total} description="Rechnungen in Prüfung" />
                <SummaryCard
                    label="Automatisch geprüft"
                    value={summary.autoMatched}
                    description="Ohne Abweichung"
                />
                <SummaryCard label="Offen" value={summary.open} description="Abweichungen zu bewerten" />
                <SummaryCard label="In Klärung" value={summary.clarification} description="Rückfragen laufen" />
                <SummaryCard label="Abgeschlossen" value={summary.finished} description="Freigegeben" />
            </div>

            <div className="mt-8 overflow-x-auto rounded-lg border border-slate-200 shadow-sm">
                <table className="min-w-full divide-y divide-slate-200 bg-white">
                    <thead className="bg-slate-50">
                        <tr>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Rechnung
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Lieferant
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Importquelle
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Rechnungsdatum
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Betrag
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Abweichungen
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Status
                            </th>
                            <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Aktion
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                        {sortedInvoices.map((invoice) => {
                            const deviatingLines = invoice.lines.filter((line) => line.matchStatus !== "MATCH");
                            const deviationCount = deviatingLines.length;
                            const statusStyle = statusStyles[invoice.status] ?? "bg-slate-100 text-slate-700 border-slate-200";
                            const importStyle = importSourceLabels[invoice.importSource] ?? {
                                label: invoice.importSource,
                                classes: "bg-slate-100 text-slate-700 border-slate-200",
                            };

                            return (
                                <tr key={invoice.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-4">
                                        <div className="font-medium text-slate-900">{invoice.invoiceNumber}</div>
                                        <div className="text-xs text-slate-500">{invoice.purchaseOrderNumber}</div>
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="font-medium">{invoice.supplierName}</div>
                                        <div className="text-xs text-slate-500">
                                            Letzte Aktualisierung {dateFormatter.format(new Date(invoice.lastUpdated))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span
                                            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium ${importStyle.classes}`}
                                        >
                                            {importStyle.label}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4">{dateFormatter.format(new Date(invoice.invoiceDate))}</td>
                                    <td className="px-4 py-4 font-medium">
                                        {currencyFormatter.format(invoice.amount)}
                                    </td>
                                    <td className="px-4 py-4">
                                        {deviationCount > 0 ? (
                                            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                                                {deviationCount} Abweichung(en)
                                            </span>
                                        ) : (
                                            <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800">
                                                keine
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-4">
                                        <span
                                            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${statusStyle}`}
                                        >
                                            {invoice.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 text-right">
                                        <Link
                                            to={`/supply/invoices/${invoice.id}`}
                                            className="inline-flex items-center rounded-md border border-blue-600 px-3 py-2 text-xs font-semibold text-blue-600 transition hover:bg-blue-50"
                                        >
                                            Details prüfen
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SummaryCard({ label, value, description }) {
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
            <p className="mt-2 text-3xl font-semibold text-slate-900">{value}</p>
            <p className="mt-1 text-xs text-slate-500">{description}</p>
        </div>
    );
}

export default InvoiceReviewListPage;
